import { AuthResponse } from '@cfa/pathway-fe-shared/xp';

import { Auth } from '@/hooks/useAuth';

import Constants from 'constants/index';

import { Country } from '@/atoms/userPrefs';
import type { OktaUserRaw } from '@/types';

/**
 * TMUA highest role for LaunchDarkly targeting (`permissionLevel`).
 * Values: Admin | Stakeholder | Operator | Leader | Trainer | Team Member.
 * Distinct from Okta `userType` (job classification; e.g. "Licensee Leader").
 */
export const getLaunchDarklyRoleLevel = (roles: Auth['roles'] = {}): string =>
  getHighestUserRole(roles);

export const getLaunchdDarklyUser = ({
  audience,
  email,
  fullName,
  isInternationalUser,
  locations,
  operatorIds,
  userType,
  country,
  state,
  roles,
}: Auth) => {
  const permissionLevel = getLaunchDarklyRoleLevel(roles);

  if (isInternationalUser) {
    return {
      anonymous: true,
      audience,
      kind: 'user',
      locations,
      operatorIds,
      userType,
      permissionLevel,
      country: country.id,
      state,
    };
  }
  return {
    audience,
    email,
    key: email,
    kind: 'user',
    locations,
    operatorIds,
    name: fullName,
    userType,
    permissionLevel,
    country: country.id,
    state,
  };
};

export const getUserInfo = (
  token: OktaUserRaw,
  language?: string,
  country?: Country,
) => {
  const { nickname, family_name, email, userType } = {
    ...token,
  };

  const fullName = `${nickname} ${family_name}`;
  const firstName = nickname;

  return {
    firstName,
    fullName,
    email,
    userType,
    language,
    country,
  };
};

export const getUserInfoFromAuth = (resp: AuthResponse) => {
  const uniqueLocations = Array.from(
    new Set(resp.user?.locations?.map(loc => loc.location.number)),
  ).filter(Boolean) as string[];
  const reducedRoles = resp.user?.locations?.reduce(
    (acc, { location, roles }) => {
      const number = location.number;
      if (!number) {
        return acc;
      }
      const updatedAcc = { ...acc };
      roles.forEach(role => {
        updatedAcc[role] = [...(updatedAcc[role] ?? []), number];
      });
      return updatedAcc;
    },
    {} as Record<string, string[]>,
  );
  const isLicensee = resp.user?.audience === Constants.USER_AUDIENCE.LICENSEE;

  const isUS = resp.user?.country === Constants.SUPPORTED_COUNTRIES.US.id;
  const selectedLocation = resp.user?.selectedLocation ?? null;
  const state: string[] | null = isUS
    ? [
        ...new Set(
          resp.user?.locations
            ?.map(loc => loc.location.state)
            .filter((s): s is string => !!s),
        ),
      ]
    : null;

  const regions = [
    ...new Set(
      resp.user?.locations
        ?.map(loc => loc.location.region)
        .filter((s): s is string => !!s),
    ),
  ];

  const markets = [
    ...new Set(
      resp.user?.locations
        ?.map(loc => loc.location.market)
        .filter((s): s is string => !!s),
    ),
  ];

  const areaSupportTeams = [
    ...new Set(
      resp.user?.locations
        ?.map(loc => loc.location.areaSupportTeam)
        .filter((s): s is string => !!s),
    ),
  ];

  const operatorIds = [
    ...new Set(
      resp.user?.locations
        ?.map(loc => loc.location.operator?.id)
        .filter((id): id is string => !!id) ?? [],
    ),
  ];

  return {
    audience: resp.user?.audience ?? '',
    cdn: resp.cdn ?? [],
    country: { id: resp.user?.country ?? '' },
    features: resp.features ?? {},
    isLicensee,
    language: resp.user?.language ?? '',
    locations: uniqueLocations,
    locationsWithRoles: resp.user?.locations ?? [],
    roles: reducedRoles ?? {},
    selectedLocation,
    state,
    regions,
    markets,
    areaSupportTeams,
    operatorIds,
    userId: resp.user?.id ?? '',
  };
};

export const getHighestUserRole = (roles: Record<string, string[]> = {}) => {
  if (Constants.USER_ROLES.ADMIN in roles) {
    return Constants.USER_ROLE_NAMES.ADMIN;
  }
  if (Constants.USER_ROLES.STAKEHOLDER in roles) {
    return Constants.USER_ROLE_NAMES.STAKEHOLDER;
  }
  if (Constants.USER_ROLES.OPERATOR in roles) {
    return Constants.USER_ROLE_NAMES.OPERATOR;
  }
  if (Constants.USER_ROLES.LEADER in roles) {
    return Constants.USER_ROLE_NAMES.LEADER;
  }
  if (Constants.USER_ROLES.TRAINER in roles) {
    return Constants.USER_ROLE_NAMES.TRAINER;
  }
  return Constants.USER_ROLE_NAMES.LOGIN;
};

export const checkUserIsAtLeastLeader = (
  roles: Record<string, string[]> = {},
) => {
  const highestUserRole = getHighestUserRole(roles);
  const atLeastLeaderRoles = [
    Constants.USER_ROLE_NAMES.ADMIN,
    Constants.USER_ROLE_NAMES.STAKEHOLDER,
    Constants.USER_ROLE_NAMES.OPERATOR,
    Constants.USER_ROLE_NAMES.LEADER,
  ];
  return atLeastLeaderRoles.includes(highestUserRole);
};
